import React from 'react';
import { blockDefForFigure } from '../../lib/rhythm/ecoRitmoConfig';
import { rhythmFigureSprite } from '../../lib/rhythm/rhythmFigureSprites';
import type { RhythmFigureId } from '../../lib/rhythm/rhythmTokens';
import { RhythmFigureIcon } from './RhythmFigureIcon';
import type { FigureCellState } from './FigureGrid';

interface EcoWagonSlotProps {
  measureIndex: number;
  slotIndex: number;
  figure: RhythmFigureId | null;
  selected: boolean;
  onSelect: () => void;
  onSetFigure: (fig: RhythmFigureId | null) => void;
  layout?: { left: string; top: string; width: string; height: string };
  variant?: 'default' | 'sprite';
  readOnly?: boolean;
  playState?: FigureCellState;
}

export function EcoWagonSlot({
  measureIndex,
  slotIndex,
  figure,
  selected,
  onSelect,
  onSetFigure,
  layout,
  variant = 'default',
  readOnly = false,
  playState = 'idle',
}: EcoWagonSlotProps) {
  const block = figure ? blockDefForFigure(figure) : null;
  const illustrated = figure != null && rhythmFigureSprite(figure) != null;

  const handleDragOver = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    const fig = e.dataTransfer.getData('text/eco-figure') as RhythmFigureId;
    if (fig) onSetFigure(fig);
  };

  const isSprite = variant === 'sprite';
  const highlighted = readOnly ? playState === 'active' || selected : selected;

  const slotStyle =
    isSprite && layout
      ? { left: layout.left, top: layout.top, width: layout.width, height: layout.height }
      : undefined;

  const wrapClass = isSprite
    ? `eco-train-sprite-slot absolute ${readOnly ? `eco-train-sprite-slot--${playState}` : ''}`
    : 'eco-wagon-wrap flex flex-col items-center shrink-0';

  const btnClass = isSprite
    ? `eco-train-sprite-slot-btn w-full h-full flex items-center justify-center transition-all ${
        highlighted ? 'eco-train-sprite-slot-btn--selected' : ''
      } ${readOnly ? `eco-train-sprite-slot-btn--${playState}` : ''}`
    : `eco-wagon w-full transition-all flex flex-col items-center justify-center ${
        selected ? 'eco-wagon--selected' : ''
      }`;

  return (
    <div className={wrapClass} style={slotStyle}>
      <button
        type="button"
        onClick={readOnly ? undefined : onSelect}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={btnClass}
        disabled={readOnly}
        tabIndex={readOnly ? -1 : 0}
        aria-label={`Compás ${measureIndex + 1}, tiempo ${slotIndex + 1}${figure ? `, ${block?.label}` : ', vacío'}`}
      >
        {figure && block ? (
          <div
            role={readOnly ? 'img' : 'button'}
            tabIndex={readOnly ? undefined : 0}
            onClick={
              readOnly
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    onSetFigure(null);
                  }
            }
            onKeyDown={
              readOnly
                ? undefined
                : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onSetFigure(null);
                    }
                  }
            }
            className={`eco-wagon-figure eco-block-placed flex items-center justify-center ${
              readOnly ? '' : 'cursor-pointer transition-transform hover:scale-[1.03]'
            } ${isSprite ? 'eco-wagon-figure--sprite' : ''} ${
              isSprite && illustrated ? 'eco-wagon-figure--illustrated' : ''
            } ${readOnly ? `eco-wagon-figure--${playState}` : ''}`}
            data-figure={figure}
            style={{ color: block.color }}
            title={readOnly ? block.label : 'Quitar figura'}
          >
            <RhythmFigureIcon
              figureId={figure}
              size={isSprite ? 52 : 64}
              color={block.color}
              bare={isSprite && illustrated}
            />
          </div>
        ) : (
          !readOnly && (
            <div className={isSprite ? 'eco-train-sprite-drop' : 'eco-wagon-drop'} aria-hidden />
          )
        )}
      </button>
      {!isSprite && <div className="eco-wagon-anchor" aria-hidden />}
    </div>
  );
}

export default EcoWagonSlot;
